import { functions } from '@app/firebase';
import UserContext from '@context/UserContext';
import Sentry from '@integrations/Sentry';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useCommonStyles from '@styles/common.style';
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import React, { useContext, useMemo } from 'react';

import QuoteCard from '../../components/quotes/QuoteCard';
import QuoteCardSkeleton from '../../components/quotes/QuoteCard.skeleton';

const getDealsByEmail = httpsCallable<{ email: string }, { deals: Array<{ deal: HubSpot.Deal; lineItems: HubSpot.LineItem[] }> }>(functions, 'http-getDealsByEmail');

const Quotes = () => {
  const userContext = useContext<UserContext>(UserContext);
  const { classes: common, theme } = useCommonStyles();

  const { data: dealsData = [], isLoading: loading } = useQuery({
    queryKey: ['dealsByEmail', userContext?.user?.email],
    queryFn: async () => {
      if (!userContext?.user?.email) {
        return [];
      }

      try {
        const result = await getDealsByEmail({ email: userContext.user.email });

        return result.data.deals || [];
      } catch (error) {
        Sentry.captureException(error);
        console.error('Failed to fetch deals:', error);
        throw error;
      }
    },
    enabled: !!userContext?.user?.email,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
  });

  const { openDeals, paidDeals, canceledDeals } = useMemo(() => {
    const open: Array<{ deal: HubSpot.Deal; lineItems: HubSpot.LineItem[] }> = [];
    const paid: Array<{ deal: HubSpot.Deal; lineItems: HubSpot.LineItem[] }> = [];
    const canceled: Array<{ deal: HubSpot.Deal; lineItems: HubSpot.LineItem[] }> = [];

    dealsData.forEach((dealData) => {
      const deal = dealData.deal;
      const isClosedWon = deal.properties?.hs_is_closed_won === 'true';
      const isClosedLost = (deal.properties as any)?.hs_is_closed_lost === 'true';

      if (isClosedWon) {
        paid.push(dealData);
      } else if (isClosedLost) {
        canceled.push(dealData);
      } else {
        open.push(dealData);
      }
    });

    return {
      openDeals: open,
      paidDeals: paid,
      canceledDeals: canceled,
    };
  }, [dealsData]);

  const getSectionConfig = (title: string) => {
    switch (title) {
      case 'Open':
        return {
          icon: FolderOpenIcon,
          color: theme.palette.primary.main,
          backgroundColor: `${theme.palette.primary.main}15`,
        };
      case 'Paid':
        return {
          icon: CheckCircleIcon,
          color: '#4CAF50',
          backgroundColor: '#4CAF5015',
        };
      case 'Canceled':
        return {
          icon: CancelIcon,
          color: theme.palette.error.main,
          backgroundColor: `${theme.palette.error.main}15`,
        };
      default:
        return {
          icon: FolderOpenIcon,
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        };
    }
  };

  const renderDealsSection = (title: string, dealsToRender: Array<{ deal: HubSpot.Deal; lineItems: HubSpot.LineItem[] }>, isFirst: boolean) => {
    if (dealsToRender.length === 0) {
      return null;
    }

    const config = getSectionConfig(title);
    const IconComponent = config.icon;

    return (
      <Box
        style={{ marginTop: isFirst ? 0 : theme.spacing(5), marginBottom: theme.spacing(4) }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
            padding: theme.spacing(1.5, 2),
            paddingBottom: theme.spacing(2),
            backgroundColor: config.backgroundColor,
            borderRadius: theme.shape.borderRadius,
          }}
        >
          <IconComponent
            style={{
              color: config.color,
              fontSize: 28,
            }}
          />
          <Typography
            component="h2"
            variant="h4"
            style={{
              fontWeight: 600,
              color: config.color,
              margin: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Grid
          container
          spacing={2}
        >
          {dealsToRender.map((dealData) => {
            const deal = dealData.deal;

            if (!deal.id) {
              return null;
            }
            const id = deal.id;
            const address = deal.properties?.staging_address || deal.properties?.dealname || 'Quote';
            const created = deal.properties?.hs_createdate;
            const installDate = deal.properties?.staging_date;

            return (
              <Grid
                key={id}
                item
                xs={12}
                md={6}
                lg={4}
                sx={{ display: 'flex', width: '100%' }}
              >
                <QuoteCard
                  id={id}
                  address={address}
                  created={created}
                  installDate={installDate}
                  lineItems={dealData.lineItems}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box
      className={common.contentSpacing}
    >
      <Typography
        component="h1"
        variant="h3Alt"
      >
        Quotes
      </Typography>
      <Typography
        variant="body1"
        style={{ fontSize: 18, marginBottom: 24 }}
      >
        Manage your quotes, make a payment and easily share with others to get on the market faster
      </Typography>

      {loading ? (
        <Grid
          container
          spacing={2}
        >
          {[...Array(6)].map((_, index) => (
            <Grid
              key={index}
              item
              xs={12}
              md={6}
              lg={4}
            >
              <QuoteCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : dealsData.length ? (
        <>
          {openDeals.length > 0 && renderDealsSection('Open', openDeals, true)}
          {paidDeals.length > 0 && renderDealsSection('Paid', paidDeals, openDeals.length === 0)}
          {canceledDeals.length > 0 && renderDealsSection('Canceled', canceledDeals, openDeals.length === 0 && paidDeals.length === 0)}
        </>
      ) : (
        <Typography
          variant="body2"
          style={{ opacity: 0.8 }}
        >
          No quotes yet.
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(Quotes);
